import { useCallback, useMemo, useState } from 'react';
import type { MailboxProviderId } from 'fym-shared/src/music24/outreach/mailboxProviders';
import {
  GuideError,
  GuideServers,
  guideErrorOf,
  isAppPasswordComplete,
  providerOf,
  serversOf,
} from '../mailboxGuide/mailboxGuideState';
import type { ImapSmtpInput } from '../outreachTypes';
import { useDetectMailboxProvider } from './useDetectMailboxProvider';
import { useMailboxes } from './useMailboxes';

export type GuidePhase = 'email' | 'guide' | 'admin' | 'checking' | 'connected';

const NO_SERVERS: GuideServers = { imapHost: '', imapPort: null, smtpHost: '', smtpPort: null };

/**
 * The state of the mailbox connect guide (design outreach-v2 o1 to o7): the address, the provider
 * (detected or picked), the password, the servers, the check and the connect. Any member connects.
 */
export function useMailboxGuide(
  accountId: number | null,
  onConnected?: (mailboxUuid: string) => void,
  /** A connected mailbox (Settings "Change the password", design outreach-v2 j3): the address and name start filled. */
  initial?: { email: string; displayName: string }
) {
  const { testImapSmtp, connectImapSmtp } = useMailboxes(accountId);
  const [phase, setPhase] = useState<GuidePhase>('email');
  const [email, setEmail] = useState(initial?.email ?? '');
  const [displayName, setDisplayName] = useState(initial?.displayName ?? '');
  const [picked, setPicked] = useState<MailboxProviderId | null>(null);
  const [password, setPassword] = useState('');
  const [manualServers, setManualServers] = useState<GuideServers>(NO_SERVERS);
  const [error, setError] = useState<GuideError | null>(null);
  const [mailboxUuid, setMailboxUuid] = useState<string | null>(null);
  // "Not Gmail?" (design outreach-v2 p2): the person says the lookup is wrong for this address.
  const [detectionDismissed, setDetectionDismissed] = useState(false);
  const { detected, isDetecting } = useDetectMailboxProvider(email);

  const detectedId = detectionDismissed ? null : (detected?.providerId ?? null);
  const providerId = picked ?? detectedId;
  const knownServers = useMemo(
    () => (providerId === null ? null : serversOf(providerId, email, detected)),
    [detected, email, providerId]
  );
  const servers = knownServers ?? manualServers;
  const provider = providerId === null ? null : providerOf(providerId);
  const passwordOk =
    provider !== null &&
    (provider.passwordKind === 'appPassword' ? isAppPasswordComplete(provider.id, password) : password !== '');
  const canConnect =
    provider !== null &&
    provider.passwordKind !== 'unsupported' &&
    passwordOk &&
    servers.imapHost !== '' &&
    servers.smtpHost !== '';

  const pick = useCallback((id: MailboxProviderId) => {
    setPicked(id);
    setError(null);
  }, []);
  const changeEmail = useCallback((value: string) => {
    setEmail(value);
    // A new address is a new lookup: the tile follows it again.
    setPicked(null);
    setDetectionDismissed(false);
  }, []);
  const dismissDetection = useCallback(() => {
    setDetectionDismissed(true);
    setPicked(null);
  }, []);
  /** A way forward of the "not supported" card (design p4): an empty address and that tile. */
  const startOver = useCallback((id: MailboxProviderId) => {
    setEmail('');
    setDetectionDismissed(false);
    setPicked(id);
    setError(null);
  }, []);
  const pickAnother = useCallback(() => {
    setPhase('email');
    setPicked(null);
    setPassword('');
    setError(null);
  }, []);

  const connect = useCallback(() => {
    if (!canConnect || providerId === null) {
      return;
    }
    const address = email.trim();
    const form: ImapSmtpInput = {
      address,
      displayName: displayName.trim() === '' ? (address.split('@')[0] ?? address) : displayName.trim(),
      imapHost: servers.imapHost,
      imapPort: servers.imapPort,
      smtpHost: servers.smtpHost,
      smtpPort: servers.smtpPort,
      username: address,
      password,
      secure: null,
      providerId,
    };
    setError(null);
    setPhase('checking');
    testImapSmtp(form)
      .then(async (result) => {
        if (!result.ok) {
          setError({ reason: result.reason, detail: result.detail });
          setPhase('guide');
          return;
        }
        const connected = await connectImapSmtp(form);
        setMailboxUuid(connected.mailboxUuid);
        setPhase('connected');
        onConnected?.(connected.mailboxUuid);
      })
      .catch((failure: unknown) => {
        setError(guideErrorOf(failure));
        setPhase('guide');
      });
  }, [canConnect, connectImapSmtp, displayName, email, onConnected, password, providerId, servers, testImapSmtp]);

  return {
    phase,
    setPhase,
    email,
    setEmail: changeEmail,
    displayName,
    setDisplayName,
    detectedId,
    dismissDetection,
    startOver,
    isDetecting,
    providerId,
    pick,
    password,
    setPassword,
    knownServers,
    manualServers,
    setManualServers,
    servers,
    canConnect,
    error,
    connect,
    pickAnother,
    mailboxUuid,
  };
}
